import { useEffect, useRef, useState, useCallback } from 'react';
import { recordStudyHeartbeat, recordPortalLogin } from '../utils/studyTracking';

const HEARTBEAT_MS = 30000;
const IDLE_THRESHOLD_MS = 120000;

/**
 * Tracks active vs idle study time while student is on course portal.
 * Shows focus popup when idle too long.
 */
export default function useStudySessionTracker(userId, subject = '') {
  const [isIdle, setIsIdle] = useState(false);
  const [showFocusPopup, setShowFocusPopup] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const loginRecordedRef = useRef(false);

  const markActive = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (isIdle) {
      setIsIdle(false);
      setShowFocusPopup(false);
    }
  }, [isIdle]);

  useEffect(() => {
    if (!userId) return undefined;

    if (!loginRecordedRef.current) {
      loginRecordedRef.current = true;
      recordPortalLogin(userId).catch(console.warn);
    }

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));

    intervalRef.current = setInterval(async () => {
      const now = Date.now();
      const idle = now - lastActivityRef.current > IDLE_THRESHOLD_MS;
      setIsIdle(idle);
      if (idle) setShowFocusPopup(true);

      try {
        await recordStudyHeartbeat(userId, {
          activeSeconds: idle ? 0 : HEARTBEAT_MS / 1000,
          idleSeconds: idle ? HEARTBEAT_MS / 1000 : 0,
          subject: idle ? '' : subject,
        });
      } catch (err) {
        console.warn('Study heartbeat failed:', err?.message);
      }
    }, HEARTBEAT_MS);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, markActive));
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [userId, subject, markActive]);

  return { isIdle, showFocusPopup, dismissFocusPopup: () => setShowFocusPopup(false) };
}
